import { createMemo, Show } from 'solid-js';
import html from 'solid-js/html';
import { type ICustomElement } from 'component-register';
import { customShadowlessElement } from '@brytdesigns/web-component-core/utils';
import { correctElementType } from '@brytdesigns/web-component-core';
import { useProduct } from '@brytdesigns/shopify-product-info-components';

import { formatMoney } from '@/lib/utils/money';

type Props = {
  /** 1-based position of the Count option within product.options. */
  optionPosition: number;
  /** This card's Count value (e.g. "450"), used both to locate the variant and as the divisor. */
  optionValue: string;
  /** Localized template with a `[price]` token, e.g. "[price]/unit". */
  unitFormat: string;
};

/** Variant shape as serialized by product-variant-json — narrower than the package's Variant type. */
type PricedVariant = {
  id: string | number;
  options: string[];
  price: number;
};

const Name = 'count-card-price';

const PRICE_TOKEN = '[price]';

const Component = (props: Props, { element }: { element: HTMLElement & ICustomElement }) => {
  const [context] = useProduct(element);

  const display = createMemo(() => {
    const product = context?.product;
    const position = Number(props.optionPosition);
    const count = parseFloat(props.optionValue);
    if (!product || !position || !Number.isFinite(count) || count <= 0) return null;

    const selected = product.selected_or_first_available_variant?.options;
    if (!selected) return null;

    // Hold every other selected option fixed; swap in this card's Count value at its position.
    const target = [...selected];
    target[position - 1] = props.optionValue;

    const variants = product.variants as PricedVariant[];
    const variant = variants.find(
      (v) => v.options.length === target.length && v.options.every((o, i) => o === target[i]),
    );
    if (!variant || typeof variant.price !== 'number') return null;

    return props.unitFormat.replace(PRICE_TOKEN, formatMoney(variant.price / count));
  });

  return html` <${Show} when=${() => display() != null}>${display}<//> `;
};

customShadowlessElement(
  Name,
  { optionPosition: 0, optionValue: '', unitFormat: `${PRICE_TOKEN}/unit` },
  correctElementType(Component),
);
